import { useState } from 'react';
import { Building2, ShieldCheck, TrendingUp, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleOAuthLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleInternoLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login-interno`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('¡Bienvenido!');
        onLogin(data.user);
        
        // Redirigir según rol
        const redirectPath = data.user.role === 'operador' ? '/operador/panel' : '/dashboard';
        window.location.href = redirectPath;
      } else {
        toast.error(data.detail || 'Error de autenticación');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-6">
      <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-12 items-center">
        {/* Left Content */}
        <div className="space-y-8">
          <div className="space-y-6">
            <img 
              src="/kompa-logo.png" 
              alt="Kompa" 
              className="h-24 w-auto mb-8"
            />
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/20 rounded-full">
              <ShieldCheck className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-medium text-yellow-500">Sistema Seguro de Retiros</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Gestiona tus <span className="text-yellow-500">retiros</span> con <span className="text-yellow-500">confianza</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-400 leading-relaxed">
              Sistema completo de procesamiento de retiros con seguimiento en tiempo real, gestión de citas y panel administrativo.
            </p>
          </div>

          <div className="space-y-4">
            <FeatureItem icon={<Building2 />} text="Proceso estructurado por etapas" />
            <FeatureItem icon={<Users />} text="Atención personalizada" />
            <FeatureItem icon={<TrendingUp />} text="Seguimiento en tiempo real" />
            <FeatureItem icon={<ShieldCheck />} text="Gestión segura de documentos" />
          </div>
        </div>

        {/* Right Card - Login Tabs */}
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-700/50 p-8 lg:p-12 space-y-8">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500/10 border border-yellow-500/20 rounded-2xl">
              <ShieldCheck className="w-8 h-8 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold text-white">Acceso al Sistema</h2>
            <p className="text-gray-400">Selecciona tu tipo de acceso</p>
          </div>

          <Tabs defaultValue="admin" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-gray-700/50">
              <TabsTrigger value="admin" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
                Admin
              </TabsTrigger>
              <TabsTrigger value="operador" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
                Gerencial / Operadores
              </TabsTrigger>
            </TabsList>

            <TabsContent value="admin" className="space-y-4 mt-6">
              <p className="text-sm text-gray-400 text-center">
                Acceso para personal administrativo
              </p>
              <Button
                data-testid="login-button-oauth"
                onClick={handleOAuthLogin}
                className="w-full h-14 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-lg"
              >
                Iniciar con Emergent
              </Button>
            </TabsContent>

            <TabsContent value="operador" className="space-y-4 mt-6">
              <form onSubmit={handleInternoLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Usuario
                  </label>
                  <Input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Tu nombre de usuario"
                    className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Contraseña
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Tu contraseña"
                    className="bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-500"
                    disabled={loading}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-lg"
                >
                  {loading ? 'Iniciando...' : 'Iniciar Sesión'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-center text-sm text-gray-500">
            Sistema exclusivo para personal autorizado de Kompa
          </p>
        </div>
      </div>
    </div>
  );
}

function FeatureItem({ icon, text }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 w-10 h-10 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex items-center justify-center text-yellow-500">
        {icon}
      </div>
      <span className="text-gray-300 font-medium">{text}</span>
    </div>
  );
}
